// KÖZÖS „csatolandó fájlok" mező a LÉTREHOZÓ formokhoz (jegy-nyitás, üzenet-írás).
//
// MIÉRT KELL egyáltalán ez a köztes állapot: a csatolmány-API a HORDOZÓHOZ kötött (`/tickets/:id/attachments`,
// `/messages/:id/attachments`), a létrehozó formon viszont még nincs id. Ezért a form csak ÖSSZEGYŰJTI a
// fájlokat, és a feltöltés a hordozó létrejötte UTÁN indul. Ez tudatos: a bájtok közvetlenül a tárba mennek,
// és a létrehozás soha nem „vár" a feltöltésre — ha egy fájl elhasal, a jegy/üzenet attól még elkészült, és a
// hiba NÉV SZERINT jelzett (a hívó dolga a részleges-hiba toast).
//
// MIÉRT `shared/`: két létrehozó form használja, azonos viselkedéssel (kliens-validáció → lista → eltávolítás).
// A feliratok FELOLDOTT stringként jönnek (nem i18n-kulcs-prefixként), hogy a típusos kulcs-ellenőrzés megmaradjon.
import { PaperClipIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useRef } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ATTACHMENT_ACCEPT, MAX_ATTACHMENTS, validateAttachments } from '@/lib/attachment-rules';

export interface PendingAttachmentsFieldProps {
  files: File[];
  onChange: (files: File[]) => void;
  /** Mentés közben a mező tiltott (a fájlok már útra kelnek). */
  disabled?: boolean;
  labels: {
    title: string;
    add: string;
    /** Az eltávolító gomb akadálymentes neve a fájlnévvel. */
    remove: (filename: string) => string;
  };
  /** FELOLDOTT hiba-toastok a kliens-oldali (UX-) validációhoz. */
  messages: { limit: string; invalidType: string; tooLarge: string };
}

export function PendingAttachmentsField({
  files,
  onChange,
  disabled = false,
  labels,
  messages,
}: PendingAttachmentsFieldProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);

  const addFiles = (picked: File[]): void => {
    const invalid = validateAttachments(picked, files.length);
    if (invalid === 'limit') {
      toast.error(messages.limit);
      return;
    }
    if (invalid === 'type') {
      toast.error(messages.invalidType);
      return;
    }
    if (invalid === 'size') {
      toast.error(messages.tooLarge);
      return;
    }
    onChange([...files, ...picked]);
  };

  return (
    <div className="space-y-2">
      {/* `block`: a `<label>` inline elem — enélkül a csatoló-gomb (inline-flex) UGYANABBA a sorba sodródik. */}
      <Label className="block">{labels.title}</Label>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ATTACHMENT_ACCEPT}
        className="hidden"
        onChange={(e) => {
          const picked = Array.from(e.target.files ?? []);
          e.target.value = ''; // ugyanaz a fájl újraválaszthatóvá válik
          if (picked.length > 0) addFiles(picked);
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => inputRef.current?.click()}
        disabled={files.length >= MAX_ATTACHMENTS || disabled}
      >
        <PaperClipIcon className="size-4" />
        {labels.add}
      </Button>
      {files.length > 0 && (
        <ul className="space-y-1.5">
          {files.map((file, i) => (
            <li
              key={`${file.name}-${String(i)}`}
              className="border-border flex items-center gap-2 rounded-md border px-2.5 py-1.5"
            >
              <PaperClipIcon className="text-muted-foreground size-3.5 shrink-0" />
              <span className="min-w-0 flex-1 truncate text-xs">{file.name}</span>
              <span className="text-subtle shrink-0 text-xs tabular-nums">
                {Math.max(1, Math.round(file.size / 1024))} kB
              </span>
              <button
                type="button"
                aria-label={labels.remove(file.name)}
                onClick={() => onChange(files.filter((_, idx) => idx !== i))}
                className="text-muted-foreground hover:text-foreground focus-visible:ring-ring shrink-0 rounded focus:outline-none focus-visible:ring-1"
              >
                <XMarkIcon className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
